/**
 * How a value a caller wrote, or a value something threw, is written into a message.
 *
 * One print for every refusal and every report of this package. A message is built while a
 * failure is being reported, so the print never throws: `String()` throws for an object with no
 * `toString` and for one whose `toString` throws, and `JSON.stringify` throws for a value that
 * holds itself and for a `bigint`, each a `TypeError` that would stand in for the failure being
 * reported.
 */

/**
 * PRINT_LENGTH_MAX is the most characters a print takes in a message, counted as a string's
 * `length` counts them: in UTF-16 code units, so a character written as a surrogate pair counts
 * two.
 *
 * A message quotes the value it refuses so a reader can find it, and a value can be anything a
 * caller wrote: a whole geometry where a box belongs, a parsed response a seam rejected with. Past
 * the cap the print ends in `…` and the count of the characters it leaves out.
 */
export const PRINT_LENGTH_MAX = 200;

/** What is written for a value no string can be made of. */
const UNPRINTABLE = "an object that cannot be printed";

/**
 * An object that states how JSON writes it, as a `Date` does. The value is `unknown` on both
 * sides: it is whatever a caller wrote, and `toJSON` answers whatever it likes.
 */
const statesItsJson = (value: object): value is { toJSON: () => unknown } =>
  "toJSON" in value && typeof value.toJSON === "function";

/**
 * write prints one value, the objects it is nested in held in `within` so a value that holds
 * itself is written once. It throws what reading the value throws: {@link printValue} is the
 * one caller, and answers for it.
 */
const write = (value: unknown, within: Set<object>): string => {
  switch (typeof value) {
    case "string":
      return JSON.stringify(value);
    case "bigint":
      return `${value}n`;
    case "symbol":
      return value.toString();
    case "function":
      return "a function";
    case "object":
      break;
    default:
      // A number as JavaScript prints it, `NaN` and the infinities included, a boolean, and
      // `undefined`.
      return String(value);
  }

  if (value === null) {
    return "null";
  }

  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }

  if (within.has(value)) {
    return "a value that holds itself";
  }

  within.add(value);
  try {
    if (statesItsJson(value)) {
      return write(value.toJSON(), within);
    }

    if (Array.isArray(value)) {
      return `[${value.map((entry: unknown) => write(entry, within)).join(",")}]`;
    }

    const members = Object.entries(value).map(
      ([key, member]: [string, unknown]) => `${JSON.stringify(key)}:${write(member, within)}`,
    );

    return `{${members.join(",")}}`;
  } finally {
    within.delete(value);
  }
};

/**
 * printValue writes any value the way a message of this package shows it. It never throws.
 *
 * A number is written as JavaScript prints it, `NaN` and the infinities included. A string is
 * quoted. An array and an object are written as JSON writes them, with no space, and with the
 * values JSON refuses or leaves out written too: `NaN` and the infinities by name, a `bigint`
 * with its `n`, `undefined`, a symbol, `a function`, and `a value that holds itself` where an
 * object is nested in itself. An `Error` is written as its name and its message. A print longer
 * than {@link PRINT_LENGTH_MAX} is cut to it, and ends in `… (N more characters)`.
 *
 * The value is `unknown` because it is whatever a caller wrote where another value belongs, or
 * whatever something threw.
 *
 * @param value The value to write.
 * @returns The print, or `an object that cannot be printed` for a value that throws as it is
 *   read: a revoked proxy, an object whose `toJSON` or whose getter throws.
 */
export const printValue = (value: unknown): string => {
  let print: string;
  try {
    print = write(value, new Set());
  } catch {
    return UNPRINTABLE;
  }

  if (print.length <= PRINT_LENGTH_MAX) {
    return print;
  }

  // The tail's own length depends on the count it states, so the count is taken against the
  // longest tail the print can carry and the head is cut to what is left: one unit sooner where
  // the cut would end on the first half of a surrogate pair, so a print never carries half a
  // character.
  const longestTail = `… (${print.length} more characters)`.length;
  let end = PRINT_LENGTH_MAX - longestTail;
  const last = print.charCodeAt(end - 1);
  if (last >= 0xd800 && last <= 0xdbff) {
    end -= 1;
  }
  const head = print.slice(0, end);

  return `${head}… (${print.length - head.length} more characters)`;
};
